import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createHotelWithIntake } from '../features/hotels/create-with-intake.js';
import { HOTELS_QUERY_KEY, type CreateHotelBody } from '../features/hotels/api.js';
import { Card, CardHeader, CardTitle, CardDescription } from '../components/ui/card.js';
import { Input } from '../components/ui/input.js';
import { Button } from '../components/ui/button.js';
import { Field, FormActions } from '../components/ui/form.js';
import { slugify } from '../lib/utils.js';

function NewHotelPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [freq, setFreq] = useState(2);
  const [ratio, setRatio] = useState(0.7);
  const [error, setError] = useState<string | null>(null);
  const [createdHotelId, setCreatedHotelId] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: (body: CreateHotelBody) => createHotelWithIntake(body),
    onSuccess: ({ hotel, intakeError }) => {
      // The org-wide hotels list is now cached for 30s (main.tsx `staleTime`),
      // so without this the hotel we just created is missing from every list
      // that reads it — dashboard, Tervek, filters, command palette — for half
      // a minute after its own creator made it. Every other hotel write
      // (update, delete, active toggle) already does this.
      void qc.invalidateQueries({ queryKey: HOTELS_QUERY_KEY });
      // Honor the form's promise ("A weboldal megadása esetén automatikus
      // intake fut le"). The hotel exists either way, but if the crawl could
      // not be started we stay put and say so — navigating away would leave
      // the user staring at a profile that never fills in.
      if (intakeError) {
        setError(
          `A hotel létrejött, de a beolvasás nem indult el: ${intakeError}. ` +
            'A hotel oldalán újrapróbálhatod.',
        );
        setCreatedHotelId(hotel.id);
        return;
      }
      void navigate({ to: '/app/hotels/$hotelId', params: { hotelId: hotel.id } });
    },
    onError: (e: Error) => setError(e.message),
  });

  function onNameChange(v: string) {
    setName(v);
    if (!slugTouched) setSlug(slugify(v));
  }

  return (
    <div className="mx-auto max-w-2xl">
      <Card>
        <CardHeader>
          <CardTitle>Új hotel</CardTitle>
          <CardDescription>
            Add meg a hotel alapadatait. Weboldal megadása esetén automatikusan elindul a weboldal
            beolvasása.
          </CardDescription>
        </CardHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            const body: CreateHotelBody = {
              name,
              slug,
              defaultPostFrequencyPerWeek: freq,
              brandSalesRatio: ratio,
            };
            if (websiteUrl) body.websiteUrl = websiteUrl;
            if (contactName) body.contactName = contactName;
            if (contactEmail) body.contactEmail = contactEmail;
            create.mutate(body);
          }}
          className="space-y-4"
        >
          <Field label="Hotel név" htmlFor="name">
            <Input id="name" value={name} required onChange={(e) => onNameChange(e.target.value)} />
          </Field>
          <Field
            label="Slug"
            htmlFor="slug"
            hint="URL-barát azonosító (csak kisbetű, kötőjel, számok)"
          >
            <Input
              id="slug"
              value={slug}
              required
              pattern="[a-z0-9-]+"
              onChange={(e) => {
                setSlug(e.target.value);
                setSlugTouched(true);
              }}
            />
          </Field>
          <Field label="Weboldal" htmlFor="website" hint="A beolvasás erre az URL-re fut">
            <Input
              id="website"
              type="url"
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
            />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Kapcsolattartó neve (a hotel részéről)" htmlFor="contact-name">
              <Input
                id="contact-name"
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
              />
            </Field>
            <Field label="Kapcsolattartó email (a hotel részéről)" htmlFor="contact-email">
              <Input
                id="contact-email"
                type="email"
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
              />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Heti posztok" htmlFor="freq">
              <Input
                id="freq"
                type="number"
                min={1}
                max={14}
                value={freq}
                onChange={(e) => setFreq(Number(e.target.value))}
              />
            </Field>
            <Field label="Brand/Sales arány" htmlFor="ratio" hint="0.7 = 70% brand, 30% sales">
              <Input
                id="ratio"
                type="number"
                min={0}
                max={1}
                step={0.05}
                value={ratio}
                onChange={(e) => setRatio(Number(e.target.value))}
              />
            </Field>
          </div>
          {error && <p className="text-body text-red-700">{error}</p>}
          {createdHotelId && (
            <Button
              type="button"
              variant="secondary"
              onClick={() =>
                void navigate({
                  to: '/app/hotels/$hotelId',
                  params: { hotelId: createdHotelId },
                })
              }
            >
              Tovább a hotel oldalára
            </Button>
          )}
          <FormActions>
            <Button
              type="button"
              variant="ghost"
              onClick={() => void navigate({ to: '/app/hotels' })}
            >
              Mégse
            </Button>
            <Button type="submit" disabled={create.isPending}>
              {create.isPending ? 'Mentés…' : 'Létrehozás'}
            </Button>
          </FormActions>
        </form>
      </Card>
    </div>
  );
}

export const Route = createFileRoute('/app/hotels/new')({
  component: NewHotelPage,
});
